import { useTranslation } from "react-i18next";
import { CircleCheck, Image, TriangleAlert } from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import { WFPButton } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatBidRequestDate } from "../../../BidRequests/utils";
import type { DeliveryView } from "../../../BidRequests/utils";

interface DeliveriesTabProps {
  deliveries: DeliveryView[];
  aggregatorName: string;
  // Delivery cannot start until the farmer organisation accepted the demand and
  // assigned stock to every commodity line.
  awaitingStock?: boolean;
}

export const DeliveriesTab = ({
  deliveries,
  aggregatorName,
  awaitingStock = false,
}: DeliveriesTabProps) => {
  const { t } = useTranslation();

  if (deliveries.length === 0) {
    return (
      <p className="rounded-lg border border-border bg-muted/40 p-4 text-muted-foreground text-sm">
        {awaitingStock
          ? t("bidRequests.detail.deliveries.awaitingStock", { aggregator: aggregatorName })
          : t("bidRequests.detail.deliveries.empty")}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {deliveries.map(delivery => (
        <Card key={delivery.uuid} className="gap-0 py-0">
          <CardContent className="flex flex-col gap-4 p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex flex-col gap-0.5">
                <h3 className="font-semibold">
                  {t("bidRequests.detail.deliveries.title", {
                    date: formatBidRequestDate(delivery.deliveredAt),
                  })}
                </h3>
                <p className="text-muted-foreground text-sm">
                  {t("bidRequests.detail.deliveries.by", { aggregator: aggregatorName })}
                </p>
              </div>
              {delivery.attachment && (
                <WFPButton variant="outline" size="sm" asChild>
                  <a href={delivery.attachment} target="_blank" rel="noopener noreferrer">
                    <Image className="size-4" />
                    {t("bidRequests.detail.deliveries.viewNote")}
                  </a>
                </WFPButton>
              )}
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("bidRequests.detail.deliveries.commodity")}</TableHead>
                  <TableHead>{t("bidRequests.detail.deliveries.delivered")}</TableHead>
                  <TableHead>{t("bidRequests.detail.deliveries.remaining")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {delivery.lines.map(line => (
                  <TableRow key={line.childOfferId}>
                    <TableCell>{line.commodityName}</TableCell>
                    <TableCell>
                      <span className="flex items-center gap-2">
                        <CircleCheck className="size-4 text-success-700" />
                        {line.delivered}
                      </span>
                    </TableCell>
                    <TableCell>
                      {line.remaining ? (
                        <span className="flex items-center gap-2 text-warning-700">
                          <TriangleAlert className="size-4" />
                          {line.remaining}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};
